"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosStorefrontThemeSchema = exports.ThemeConfigSchema = exports.AccountConfigSchema = exports.SignInConfigSchema = exports.ThemeSettingsSchema = exports.FooterConfigSchema = exports.FooterColumnSchema = exports.NavigationSchema = exports.MenuItemSchema = exports.LinkRefSchema = exports.PageSchema = exports.SectionInstanceSchema = exports.BlockSchema = void 0;
const zod_1 = require("zod");
exports.BlockSchema = zod_1.z.object({
    id: zod_1.z.string(),
    type: zod_1.z.string(),
    settings: zod_1.z.record(zod_1.z.unknown()).default({}),
});
exports.SectionInstanceSchema = zod_1.z.object({
    id: zod_1.z.string(),
    type: zod_1.z.string(),
    visible: zod_1.z.boolean().default(true),
    settings: zod_1.z.record(zod_1.z.unknown()).default({}),
    blocks: zod_1.z.array(exports.BlockSchema).default([]),
});
exports.PageSchema = zod_1.z.object({
    id: zod_1.z.string(),
    slug: zod_1.z.string(),
    title: zod_1.z.string().default(''),
    type: zod_1.z.enum(['home', 'page', 'system']).default('page'),
    // SP4 additive: per-page visibility (independent of site-wide publish) + page kind.
    status: zod_1.z.enum(['draft', 'published']).default('published'),
    pageKind: zod_1.z.enum(['home', 'freeform', 'policy', 'contact', 'faq', 'ad']).default('freeform'),
    seo: zod_1.z.object({
        metaTitle: zod_1.z.string().optional(),
        metaDescription: zod_1.z.string().optional(),
        ogImageUrl: zod_1.z.string().optional(),
    }).default({}),
    sections: zod_1.z.array(exports.SectionInstanceSchema).default([]),
});
// SP4 additive: navigation menu tree + structured footer.
exports.LinkRefSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ kind: zod_1.z.literal('page'), pageId: zod_1.z.string() }),
    zod_1.z.object({ kind: zod_1.z.literal('category'), categoryId: zod_1.z.string() }),
    zod_1.z.object({ kind: zod_1.z.literal('builtin'), route: zod_1.z.enum(['home', 'cart', 'account', 'b2b-apply', 'verify', 'careers', 'brands', 'book']) }),
    zod_1.z.object({ kind: zod_1.z.literal('external'), url: zod_1.z.string(), newTab: zod_1.z.boolean().optional() }),
]);
exports.MenuItemSchema = zod_1.z.lazy(() => zod_1.z.object({
    id: zod_1.z.string(),
    label: zod_1.z.string(),
    link: exports.LinkRefSchema.optional(),
    children: zod_1.z.array(exports.MenuItemSchema).optional(),
}));
exports.NavigationSchema = zod_1.z.object({
    primary: zod_1.z.array(exports.MenuItemSchema).default([]),
    // Show the auto category menu row. undefined = legacy (categories show only when there is
    // no custom nav); true = always show categories (alongside a custom nav); false = never.
    showCategories: zod_1.z.boolean().optional(),
});
exports.FooterColumnSchema = zod_1.z.object({
    id: zod_1.z.string(),
    heading: zod_1.z.string().default(''),
    items: zod_1.z.array(exports.MenuItemSchema).default([]),
});
exports.FooterConfigSchema = zod_1.z.object({
    columns: zod_1.z.array(exports.FooterColumnSchema).max(4).default([]),
    info: zod_1.z.object({
        showLogo: zod_1.z.boolean().default(true),
        tagline: zod_1.z.string().optional(),
        socials: zod_1.z.array(zod_1.z.object({
            platform: zod_1.z.enum(['instagram', 'facebook', 'tiktok', 'x', 'youtube', 'linkedin']),
            url: zod_1.z.string(),
        })).default([]),
        showPoweredBy: zod_1.z.boolean().default(true),
        showPaymentBadges: zod_1.z.boolean().default(true),
        // SP5d: which payment badges to show (undefined → the classic four).
        paymentMethods: zod_1.z.array(zod_1.z.enum(['visa', 'mastercard', 'amex', 'discover', 'applepay', 'googlepay', 'paypal'])).optional(),
    }).passthrough().default({}),
    // SP5b: footer colour controls (all optional → falls back to the dark default).
    bgType: zod_1.z.enum(['solid', 'gradient']).optional(),
    bgColor: zod_1.z.string().optional(),
    bgGradient: zod_1.z.object({
        angle: zod_1.z.number().default(135),
        stops: zod_1.z.array(zod_1.z.object({ color: zod_1.z.string(), position: zod_1.z.number() })).default([]),
    }).optional(),
    textColor: zod_1.z.string().optional(),
    headingColor: zod_1.z.string().optional(),
    linkColor: zod_1.z.string().optional(),
}).default({});
// SP1: the existing branding fields, wrapped. SP2 adds type scale / buttons / spacing (additive).
exports.ThemeSettingsSchema = zod_1.z.object({
    displayName: zod_1.z.string().max(200).optional(),
    tagline: zod_1.z.string().max(300).optional(),
    logoUrl: zod_1.z.string().optional(),
    faviconUrl: zod_1.z.string().optional(),
    primaryColor: zod_1.z.string().optional(),
    primaryColorDark: zod_1.z.string().optional(),
    accentColor: zod_1.z.string().optional(),
    backgroundColor: zod_1.z.string().optional(),
    // SP5c: whole-site background can be solid (backgroundColor) or a gradient.
    backgroundType: zod_1.z.enum(['solid', 'gradient']).optional(),
    backgroundGradient: zod_1.z.object({
        angle: zod_1.z.number().default(135),
        stops: zod_1.z.array(zod_1.z.object({ color: zod_1.z.string(), position: zod_1.z.number() })).default([]),
    }).optional(),
    inkColor: zod_1.z.string().optional(),
    displayFont: zod_1.z.string().optional(),
    bodyFont: zod_1.z.string().optional(),
    headingWeight: zod_1.z.enum(['normal', 'medium', 'semibold', 'bold']).default('semibold'),
    fontScale: zod_1.z.enum(['compact', 'normal', 'spacious']).default('normal'),
    buttonRadius: zod_1.z.enum(['sharp', 'rounded', 'pill']).default('pill'),
    buttonStyle: zod_1.z.enum(['solid', 'outline']).default('solid'),
    cornerRadius: zod_1.z.enum(['sharp', 'soft', 'round']).default('round'),
    density: zod_1.z.enum(['compact', 'cozy', 'spacious']).default('cozy'),
    containerWidth: zod_1.z.enum(['standard', 'wide']).default('standard'),
}).default({});
// Customer sign-in page design (storefront /account/login). Additive.
exports.SignInConfigSchema = zod_1.z.object({
    headline: zod_1.z.string().max(120).optional(),
    subtext: zod_1.z.string().max(300).optional(),
    layout: zod_1.z.enum(['card', 'split', 'minimal']).optional(),
    image: zod_1.z.string().optional(),
    showLogo: zod_1.z.boolean().optional(),
});
exports.AccountConfigSchema = zod_1.z.object({ signIn: exports.SignInConfigSchema.optional() });
exports.ThemeConfigSchema = zod_1.z.object({
    settings: exports.ThemeSettingsSchema,
    pages: zod_1.z.array(exports.PageSchema).default([]),
    header: zod_1.z.array(exports.SectionInstanceSchema).default([]),
    footer: zod_1.z.array(exports.SectionInstanceSchema).default([]),
    // SP4 additive: owner-built nav + structured footer (optional; legacy stores omit them).
    navigation: exports.NavigationSchema.optional(),
    footerConfig: exports.FooterConfigSchema.optional(),
    // Customer account pages (sign-in) — designed with the theme.
    account: exports.AccountConfigSchema.optional(),
    toggles: zod_1.z.record(zod_1.z.unknown()).default({}),
    legal: zod_1.z.record(zod_1.z.unknown()).default({}),
    seo: zod_1.z.record(zod_1.z.unknown()).default({}),
    emails: zod_1.z.record(zod_1.z.unknown()).default({}),
});
exports.APosStorefrontThemeSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    version: zod_1.z.number().default(1),
    published: exports.ThemeConfigSchema,
    draft: exports.ThemeConfigSchema.nullable().default(null),
    publishedAt: zod_1.z.number().default(0),
    draftUpdatedAt: zod_1.z.number().nullable().default(null),
    updatedAt: zod_1.z.number().default(0),
});
//# sourceMappingURL=storefront-theme.schema.js.map